/**
 * #16a — pre-select a colour on the PDP from a `?colour=` URL parameter.
 *
 * Collection tiles link with `?colour=<value>` (Rule A swatches via ProductCard.handleSwatchChange
 * in main.js, Rule B per-colour tiles via product-card.liquid). This selects that colour and
 * NOTHING else, so size/width/flex stay open for the customer to choose.
 *
 * Why this is JS and not Liquid: Liquid has no access to arbitrary query parameters. `request`
 * exposes path/host/page_type only — the query string is not available. The one param Shopify
 * surfaces is `?variant=`, and that is exactly what we must avoid: it sets
 * product.selected_variant, which pre-selects EVERY option (measured: 0 checked inputs without
 * it, 2 — Color AND Size — with it) and breaks #1's "never pre-select when alternatives exist".
 *
 * Why this cannot flash: because no `?variant=` is sent, the server renders the picker with
 * nothing selected. This script only ever ADDS a selection. There is no wrong state to clear, so
 * there is no window in which the customer can see a size they did not choose.
 *
 * State stays consistent because the selection is made by dispatching a real `change` event,
 * which is what variant-picker.js listens for (see its connectedCallback). Setting `.checked`
 * without the event would leave the component's internal selectedOptions out of sync and can
 * leave add-to-cart in a wrong state.
 */
(() => {
  const params = new URLSearchParams(window.location.search);
  const wanted = params.get('colour');
  if (!wanted) return;

  const normalise = (value) => String(value).trim().toLowerCase();
  const target = normalise(wanted);

    // Nudge the gallery to this colour's media group.
  //
  // Needed because variant-picker only dispatches `on:variant:change` once a COMPLETE variant
  // resolves, and here just the colour is selected — size is deliberately left open. Without
  // this the gallery keeps showing the product's default media, so a customer arriving from a
  // blue tile sees the white shoe.
  // media-gallery.js only runs setActiveMediaGroup() off that event, so it is called directly.
  const showColourMedia = (value) => {
    const gallery = document.querySelector('media-gallery');
    if (!gallery || !gallery.hasAttribute('data-media-grouping-enabled')) return;
    if (typeof gallery.setActiveMediaGroup !== 'function') return;
    try {
      gallery.setActiveMediaGroup(value);
    } catch (e) {
      // Never let a gallery quirk break variant selection, which is the important half.
    }
  };

  /**
   * Selects the colour if a matching control exists.
   * @returns {boolean} True once a match has been applied.
   */
  const applyColour = () => {
    const picker = document.querySelector('variant-picker');
    if (!picker) return false;

    // Radio / swatch style.
    const radios = picker.querySelectorAll('input[type="radio"]');
    for (let i = 0; i < radios.length; i += 1) {
      const radio = radios[i];
      if (normalise(radio.value) === target) {
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        showColourMedia(radio.value);
        return true;
      }
    }

    // Dropdown style — colour normally renders as swatches, but a merchant can switch it.
    const selects = picker.querySelectorAll('select');
    for (let i = 0; i < selects.length; i += 1) {
      const select = selects[i];
      const match = Array.from(select.options).find((o) => normalise(o.value) === target);
      if (match) {
        if (select.value !== match.value) {
          select.value = match.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        showColourMedia(match.value);
        return true;
      }
    }

    return false;
  };

  // The picker is a custom element loaded with `defer`, so wait for it to be defined before
  // touching it — otherwise the change event fires before any listener is attached and is lost.
  const run = () => {
    if (applyColour()) return;
    // The element can exist before its children are laid out on a slow parse; retry once on the
    // next frame rather than polling.
    requestAnimationFrame(applyColour);
  };

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('variant-picker').then(run);
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
